import {
  CustomerLookupResponse,
  CustomerLookupResponseAccounts,
  CustomerLookupResponseAccount,
} from 'packages/tackle-api/src/co-sell-msft/customerLookup';
import {
  CustomerSearchFormStep,
  FORM_STEP_CONTAINER_MIN_WIDTH,
  FORM_STEP_CONTAINER_WIDTH,
  TYPOGRAPHY_STYLES,
} from '../../constants';
import type { CustomerAccountDetailsType } from '../../customerAccountTransformSchema';

// COMPONENTS
import { Tabs } from '@tackle-io/platform-ui';
import { Typography, useTheme } from 'ui';
import { Box } from 'vendor/material';
import BackLink from '../../../components/BackLink/BackLink';
import CustomerAccountItem from '../CustomerAccountItem/CustomerAccountItem';

export default function CustomerAccountsList({
  customerSearchData,
  handleAddToForm,
  setFormStep,
  activeTypeTabIndex,
  setActiveTypeTabIndex,
  setSelectedAccount,
}: {
  customerSearchData: CustomerLookupResponse;
  setFormStep: (step: CustomerSearchFormStep) => void;
  handleAddToForm: (customerDetails: CustomerAccountDetailsType) => void;
  activeTypeTabIndex: number;
  setActiveTypeTabIndex: (index: number) => void;
  setSelectedAccount: (account: CustomerLookupResponseAccount) => void;
}) {
  const theme = useTheme();
  const managedAccounts = customerSearchData?.managedAccounts;
  const unManagedAccounts = customerSearchData?.unManagedAccounts;
  const totalCustomerMatches =
    managedAccounts?.length + unManagedAccounts?.length || 0;

  const accountTypeTabs = [
    {
      label: `Microsoft managed (${managedAccounts?.length})`,
      value: 'managedAccounts',
    },
    {
      label: `Microsoft unmanaged (${unManagedAccounts?.length})`,
      value: 'unManagedAccounts',
    },
    // TODO: uncomment when we add other accounts type
    /*
    {
      label: `Other (${otherAccounts?.length})`,
      value: 'other',
    }, 
    */
  ];

  const selectedAccountsList = customerSearchData?.[
    accountTypeTabs?.[activeTypeTabIndex]?.value
  ] as CustomerLookupResponseAccounts;

  const selectedAccountTypeLabel = {
    0: 'Microsoft managed',
    1: 'Microsoft unmanaged',
    2: 'other',
  };

  const minWidth = theme.breakpoints.up('sm')
    ? FORM_STEP_CONTAINER_MIN_WIDTH
    : 'auto';

  return (
    <Box minWidth={minWidth} width={FORM_STEP_CONTAINER_WIDTH}>
      <BackLink
        text={'Search again'}
        onClick={() => {
          setFormStep(CustomerSearchFormStep.SEARCH);
        }}
      />

      <Box mt={2}>
        <>
          <>
            <Typography mb={2} style={{ ...TYPOGRAPHY_STYLES }}>
              <strong>{totalCustomerMatches}</strong> matches found
            </Typography>
            <hr />
            <Box mt={2}>
              <Tabs
                tabs={accountTypeTabs}
                value={activeTypeTabIndex}
                onChange={(_, selectedTabIndex) => {
                  setActiveTypeTabIndex(selectedTabIndex);
                }}
              />
            </Box>
            {totalCustomerMatches === 0 ? (
              <Box p={2} mt={3}>
                <Typography style={{ ...TYPOGRAPHY_STYLES, fontSize: 12 }}>
                  No accounts found. Try changing your search criteria.
                </Typography>
              </Box>
            ) : (
              <>
                {selectedAccountsList?.length === 0 && (
                  <>
                    <Box p={2} mt={2}>
                      <Typography
                        style={{ ...TYPOGRAPHY_STYLES, fontSize: 12 }}
                      >
                        No{' '}
                        {selectedAccountTypeLabel?.[activeTypeTabIndex] || ''}{' '}
                        accounts found. Try changing your search criteria.
                      </Typography>
                    </Box>
                  </>
                )}
                {selectedAccountsList?.map(
                  (account: CustomerLookupResponseAccount, index) => {
                    return (
                      <div
                        key={`customer-account-item-${account?.customerAccountDetails?.msp_accountnumber}-${index}`}
                        data-testid={`mockCustomerAccount-${index}`}
                      >
                        <CustomerAccountItem
                          data-testid={`mockCustomerAccount-${index}`}
                          account={account}
                          onSelect={handleAddToForm}
                          setFormStep={setFormStep}
                          setSelectedAccount={setSelectedAccount}
                        />
                      </div>
                    );
                  },
                )}
              </>
            )}
          </>
        </>
      </Box>
    </Box>
  );
}
